import React, { useState, useEffect } from 'react';
import { Plus, Check, Trash2, RefreshCw, Eye, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { accountsAPI, meliAPI, syncAPI } from '../services/api';
import { getAccountId, setAccountId } from '../services/account';

const AccountSelector = ({ userData, onAccountChange }) => {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  // Which account the screens are reading. Separate from is_active, which is
  // the server-side default the background jobs fall back to.
  const [viewing, setViewing] = useState(getAccountId());
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const [accountsList, active] = await Promise.all([
        accountsAPI.list(),
        accountsAPI.getActive()
      ]);
      
      const list = accountsList.accounts || [];
      setAccounts(list);
      setActiveAccount(active.account);

      // A selection left over from an account that has since been removed
      // would scope every request to a 404.
      const chosen = getAccountId();
      if (chosen && !list.some((a) => a.id === chosen)) {
        setAccountId(null);
        setViewing(null);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (accountId) => {
    setAccountId(accountId);
    setViewing(accountId);
    setShowDialog(false);
    // Reloaded rather than refetched: every screen holds its own data, and
    // showing one of them another brand's numbers is the whole bug this
    // selector exists to avoid.
    if (onAccountChange) onAccountChange();
    else window.location.reload();
  };

  const handleSyncAll = async () => {
    try {
      const data = await syncAPI.triggerAll();
      toast({
        title: 'Sincronización iniciada',
        description: `${data.cuentas} cuentas, una después de otra. Puede tardar varios minutos.`,
      });
      setShowDialog(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo iniciar la sincronización',
        variant: 'destructive'
      });
    }
  };

  const handleSetActive = async (accountId) => {
    try {
      await accountsAPI.setActive(accountId);
      toast({
        title: '¡Cuenta activada!',
        description: 'Los datos se actualizarán automáticamente',
      });
      await loadAccounts();
      if (onAccountChange) {
        onAccountChange();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo cambiar de cuenta',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cuenta de Mercado Libre?')) {
      return;
    }

    try {
      await accountsAPI.delete(accountId);
      toast({
        title: '¡Cuenta eliminada!',
        description: 'La cuenta se eliminó correctamente',
      });
      await loadAccounts();
      if (onAccountChange) {
        onAccountChange();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la cuenta',
        variant: 'destructive'
      });
    }
  };

  const handleAddAccount = async () => {
    if (!newAccountName.trim()) {
      toast({
        title: 'Error',
        description: 'Ingresa un nombre para la cuenta',
        variant: 'destructive'
      });
      return;
    }

    const authUrl = await meliAPI.getAuthUrl('MX', newAccountName);
    window.location.href = authUrl;
  };

  const handleReconnect = async (account) => {
    const authUrl = await meliAPI.getAuthUrl(
      account.country || 'MX', account.account_name || 'Cuenta Principal'
    );
    window.location.href = authUrl;
  };

  const viewingAccount = accounts.find((a) => a.id === viewing);
  const shownName = (viewingAccount || activeAccount)?.account_name;

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled>
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        Cargando...
      </Button>
    );
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-gray-700 hover:bg-gray-800">
          {shownName ? (
            <>
              <Check className="w-4 h-4 mr-2 text-green-400" />
              {shownName}
            </>
          ) : (
            'Seleccionar Cuenta'
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>Cuentas de Mercado Libre</DialogTitle>
          <DialogDescription className="text-gray-400">
            Elige qué cuenta estás viendo. Publicaciones, órdenes y métricas se
            filtran por la cuenta seleccionada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* List of accounts */}
          {accounts.length > 0 ? (
            <div className="space-y-2">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    account.id === viewing
                      ? 'bg-indigo-600/20 border-indigo-500/50'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-medium">{account.account_name}</p>
                    <p className="text-xs text-gray-400">
                      {account.country} • ID: {account.meli_user_id}
                      {account.is_active && ' • predeterminada'}
                    </p>
                    {account.needs_reconnect && (
                      <button
                        type="button"
                        onClick={() => handleReconnect(account)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 underline"
                        title={account.last_refresh_error || 'El token de Mercado Libre necesita reconexión'}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Reconectar
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {account.id === viewing ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                        <Eye className="w-3 h-3" />
                        Viendo
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleView(account.id)}
                        className="bg-indigo-600 hover:bg-indigo-500"
                      >
                        Ver
                      </Button>
                    )}
                    {!account.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetActive(account.id)}
                        className="border-gray-700 hover:bg-gray-800"
                        title="La cuenta que usan las tareas en segundo plano"
                      >
                        Predeterminar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(account.id)}
                      className="border-red-700 hover:bg-red-900/20 text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                {viewing ? (
                  <button
                    onClick={() => handleView(null)}
                    className="text-xs text-gray-400 hover:text-gray-200 underline"
                  >
                    Volver a la cuenta predeterminada
                  </button>
                ) : <span />}
                {accounts.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSyncAll}
                    className="border-gray-700 hover:bg-gray-800"
                    title="Sincroniza las cuentas una después de otra"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sincronizar todas
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">
              No hay cuentas conectadas
            </p>
          )}

          {/* Add new account */}
          <div className="border-t border-gray-700 pt-4">
            <Label htmlFor="accountName" className="text-gray-300 mb-2 block">
              Agregar nueva cuenta
            </Label>
            <div className="flex gap-2">
              <Input
                id="accountName"
                placeholder="Nombre de la cuenta (ej: Tienda Principal)"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
              />
              <Button
                onClick={handleAddAccount}
                className="bg-indigo-600 hover:bg-indigo-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Conectar
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Serás redirigido a Mercado Libre para autorizar el acceso
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSelector;
